import React, {FC} from "react";
import {
    Box, Grid,
    SliderValueLabelProps,
    SvgIcon,
    Tooltip,
    tooltipClasses,
    TooltipProps,
    Typography
} from "@mui/material";
import {GenerationParams} from "../providers/useModel";
import {styled} from "@mui/material/styles";
import {GradientButton, GradientSlider} from "./GradientComponents";
import InfoIcon from "../icons/InfoIcon";
import ArrowCounterClock from "../icons/ArrowCounterClock";

const parameterInfo = {
    "max_new_tokens": "The maximum number of new tokens to generate in addition to input tokens.",
    "temperature": "Randomness of the model, lower values make the model more deterministic, higher values makes model more creative.",
    "penalty_alpha": "Balances model confidence with previous generated tokens.",
    "top_k": "The number of tokens to consider for each step of the generation process.",
    "top_p": "The cumulative probability of the most likely tokens to consider for each step of the generation process.",
    "repetition_penalty": "Penalize new tokens based on their repetition in the generated text.",
    "compute_scores": "Whether to compute the scores of the generated tokens.",
    "do_sample": "Whether to use sampling or greedy decoding for generation.",
}

interface GenerationSettingsProps {
    generationParams: GenerationParams;
    setGenerationParams: (params?: GenerationParams) => void;
}

interface ParamSliderProps {
    paramName: keyof GenerationParams;
    generationParams: GenerationParams;
    setGenerationParams: (params: GenerationParams) => void;
    min: number;
    max: number;
    step: number;
}

const LightTooltip = styled(({className, ...props}: TooltipProps) => (
    <Tooltip {...props} classes={{popper: className}}/>
))(({theme}) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: '#FFFFFF',
        boxShadow: '0px 12px 16px -4px #0F1A2A1',
        borderRadius: 8,
        border: '1px solid #E2E8F0',
        gap: 8,
        color: '#27364B',
        fontSize: 11,
    },
}));

const ValueLabelComponent = (props: SliderValueLabelProps) => {
    const {children, value} = props;

    return (
        <LightTooltip enterTouchDelay={0}
                      id={`generation-param-slider-${value}`}
                      placement="bottom"
                      title={value}
        >
            {children}
        </LightTooltip>
    );
}

const shouldDisableParam = (paramName: keyof GenerationParams, generationParams: GenerationParams) => {
    // const value = generationParams[paramName]
    return false;
    // if (paramName === "top_k" || paramName === "penalty_alpha" || paramName === "max_new_tokens") {
    //     return false
    // } else if (generationParams.penalty_alpha > 0.0) {
    //     return true
    // } else {
    //     return false
    // }
}

const getTopKMax = (generationParams: GenerationParams) => {
    return 50
}


const ParamSlider: FC<ParamSliderProps> = ({
                                               paramName,
                                               generationParams,
                                               setGenerationParams,
                                               min, max, step
                                           }) => {

    const handleSliderChange = () => (
        event: Event,
        newValue: number | number[]
    ) => {
        let val: number;
        if (Array.isArray(newValue)) {
            val = newValue[0]
        } else {
            val = newValue
        }

        setGenerationParams({...generationParams, [paramName]: val})
    };

    const value = generationParams[paramName];
    if (typeof value !== "number") {
        throw new Error("ParamSlider value must be a number. Got " + typeof value + " instead. Param: " + paramName + " Value: " + value + ".")
    }

    return (
        <Box>
            <Box display={"flex"} alignItems={"center"}>
                <Typography variant="body2" gutterBottom component={"span"}>
                    {paramName.split("_").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ")}
                </Typography>
                <Tooltip title={parameterInfo[paramName]} followCursor={true}>
                    <SvgIcon style={{marginLeft: 4, marginBottom: 4}}>
                        <InfoIcon/>
                    </SvgIcon>
                </Tooltip>
            </Box>
            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={10} lg={12}>
                    <GradientSlider
                        size={"small"}
                        value={value}
                        onChange={handleSliderChange()}
                        step={step}
                        min={min}
                        max={max}
                        disabled={shouldDisableParam(paramName, generationParams)}
                        valueLabelDisplay={"on"}
                        slots={{
                            valueLabel: ValueLabelComponent,
                        }}
                        sx={{mb: 1}}
                    />
                </Grid>
            </Grid>
        </Box>
    )
}


const GenerationSettings: FC<GenerationSettingsProps> = ({
                                                             generationParams = {
                                                                 max_new_tokens: 128,
                                                                 temperature: 0.7,
                                                                 top_k: 50,
                                                                 top_p: 0.9,
                                                                 repetition_penalty: 1.0
                                                             },
                                                             setGenerationParams,
                                                         }) => {
    return (
        <Grid container direction={"column"} style={{height: '100%'}}>
            <Grid item display={"flex"} justifyContent={"flex-start"} flexDirection={"column"}>
                <ParamSlider paramName={"max_new_tokens"}
                             generationParams={generationParams}
                             setGenerationParams={setGenerationParams}
                             min={1}
                             max={1024}
                             step={1}/>
                <ParamSlider paramName={"temperature"}
                             generationParams={generationParams}
                             setGenerationParams={setGenerationParams}
                             min={0.0}
                             max={1.0}
                             step={0.01}/>
                <ParamSlider paramName={"top_k"}
                             generationParams={generationParams}
                             setGenerationParams={setGenerationParams}
                             min={0}
                             max={getTopKMax(generationParams)}
                             step={1}/>
                <ParamSlider paramName={"top_p"}
                             generationParams={generationParams}
                             setGenerationParams={setGenerationParams}
                             min={0.0}
                             max={1.0}
                             step={0.01}/>
                <ParamSlider paramName={"repetition_penalty"}
                             generationParams={generationParams}
                             setGenerationParams={setGenerationParams}
                             min={1.0}
                             max={1.2}
                             step={0.01}/>
            </Grid>
            <Grid item
                // sx={{mb: 2, mt: 6}}
                  alignSelf={"center"}
                  justifyContent={"center"} alignItems={"center"} display={"flex"}>
                <GradientButton startIcon={<ArrowCounterClock/>}
                                onClick={() => {
                                    setGenerationParams();
                                }}
                                style={{
                                    minWidth: 140,
                                    maxWidth: 220,
                                    padding: 12,
                                    marginTop: 28,
                                    alignSelf: "center",
                                }}>
                    <Typography variant={"body2"}>Reset Parameters</Typography>
                </GradientButton>
            </Grid>
        </Grid>
    );
};

export default GenerationSettings;
